import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ListingCard } from '@/components/ListingCard';
import { BreedCard } from '@/components/BreedCard';
import { api } from '@/lib/api';
import { Search, ArrowRight, Dog, BookOpen, Sparkles, PawPrint } from 'lucide-react';
import { SEO } from '@/components/SEO';

const statFormatter = new Intl.NumberFormat('en-US');

function normalizeCount(value) {
  const numeric = Number(value);
  return Number.isFinite(numeric) && numeric >= 0 ? numeric : null;
}

function formatStatCount(value) {
  return value === null || value === undefined ? '—' : statFormatter.format(value);
}

function pluralize(value, singular, plural = `${singular}s`) {
  return value === 1 ? singular : plural;
}

export default function HomePage() {
  const [featuredListings, setFeaturedListings] = useState([]);
  const [popularBreeds, setPopularBreeds] = useState([]);
  const [siteStats, setSiteStats] = useState({ activeListings: null, breeds: null });
  const [searchQuery, setSearchQuery] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    api.getFeaturedListings().then(r => setFeaturedListings(r.data.listings || [])).catch(() => {});
    api.getListings({ page: 1, limit: 1 }).then(r => {
      const activeListings = normalizeCount(r.data.total);
      setSiteStats(prev => ({ ...prev, activeListings: activeListings ?? prev.activeListings }));
    }).catch(() => {});
    api.getBreeds().then(r => {
      const breeds = r.data.breeds || [];
      setPopularBreeds(breeds.slice(0, 6));
      setSiteStats(prev => ({ ...prev, breeds: prev.breeds ?? breeds.length }));
    }).catch(() => {});
  }, []);

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) navigate(`/dogs-for-sale?search=${encodeURIComponent(searchQuery)}`);
  };

  return (
    <div className="animate-fade-in" data-testid="home-page">
      <SEO
        title="Find Your Perfect Dog"
        description="DoggoHub is your trusted marketplace to find dogs for sale near you. Browse puppies, explore breeds, and take our dog matcher quiz."
        keywords="dogs for sale near me, puppies for sale, dog breeders near me, buy a dog online, puppy marketplace"
      />
      {/* Hero Section */}
      <section className="relative overflow-hidden bg-white" data-testid="hero-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-24">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
            <div className="lg:col-span-5 space-y-6">
              <div className="inline-flex items-center gap-2 bg-blue-50 rounded-full px-4 py-1.5">
                <PawPrint className="w-4 h-4 text-[#4A90E2]" />
                <span className="text-sm font-medium text-[#4A90E2]">Trusted by 10,000+ dog lovers</span>
              </div>
              <h1 className="font-['Nunito'] font-extrabold text-4xl sm:text-5xl lg:text-6xl text-[#1A1D20] tracking-tight leading-[1.1]">
                Find Your
                <span className="text-[#4A90E2]"> Perfect </span>
                Dog
              </h1>
              <p className="text-base sm:text-lg text-[#5C636A] leading-relaxed max-w-md">
                Browse thousands of dogs from verified breeders. Discover the breed that matches your lifestyle.
              </p>
              <form onSubmit={handleSearch} className="flex gap-2 max-w-md">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#5C636A]" />
                  <Input
                    placeholder="Search breeds, location..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-10 h-12 rounded-xl bg-gray-50 border-gray-200 focus:ring-[#4A90E2] text-sm"
                    data-testid="hero-search-input"
                  />
                </div>
                <Button
                  type="submit"
                  className="h-12 px-6 rounded-xl bg-[#F5A623] hover:bg-[#e09515] text-white font-semibold shadow-[0_4px_14px_rgb(245,166,35,0.4)] cta-button"
                  data-testid="hero-search-button"
                >
                  Search
                </Button>
              </form>
              <div className="flex items-center gap-6 pt-2">
                <div className="text-center">
                  <p className="font-['Nunito'] font-extrabold text-2xl text-[#1A1D20]">{formatStatCount(siteStats.activeListings)}</p>
                  <p className="text-xs text-[#5C636A]">Active Listings</p>
                </div>
                <div className="w-px h-10 bg-gray-200" />
                <div className="text-center">
                  <p className="font-['Nunito'] font-extrabold text-2xl text-[#1A1D20]">{formatStatCount(siteStats.breeds)}</p>
                  <p className="text-xs text-[#5C636A]">Breeds</p>
                </div>
                <div className="w-px h-10 bg-gray-200" />
                <div className="text-center">
                  <p className="font-['Nunito'] font-extrabold text-2xl text-[#7ED321]">98%</p>
                  <p className="text-xs text-[#5C636A]">Happy Owners</p>
                </div>
              </div>
            </div>
            <div className="lg:col-span-7 relative">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-4">
                  <img
                    src="https://images.pexels.com/photos/6773171/pexels-photo-6773171.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
                    alt="Happy dog running"
                    className="w-full aspect-[3/4] object-cover rounded-3xl shadow-[0_20px_40px_rgb(0,0,0,0.08)]"
                  />
                </div>
                <div className="space-y-4 pt-8">
                  <img
                    src="https://images.unsplash.com/photo-1706745262357-5ecaa3154433?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzJ8MHwxfHNlYXJjaHwxfHxjdXRlJTIwcHVwcHklMjBwb3J0cmFpdHxlbnwwfHx8fDE3NzQxOTk5OTl8MA&ixlib=rb-4.1.0&q=85"
                    alt="Cute puppy"
                    className="w-full aspect-square object-cover rounded-3xl shadow-[0_20px_40px_rgb(0,0,0,0.08)]"
                  />
                  <img
                    src="https://images.pexels.com/photos/7516843/pexels-photo-7516843.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
                    alt="Person with dog"
                    className="w-full aspect-[4/3] object-cover rounded-3xl shadow-[0_20px_40px_rgb(0,0,0,0.08)]"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
          {[
            { icon: Dog, title: 'Browse Dogs', desc: 'Find dogs from verified breeders near you', color: '#4A90E2', bg: 'bg-blue-50', link: '/dogs-for-sale' },
            { icon: Sparkles, title: 'Dog Matcher', desc: 'Take our quiz to find your perfect match', color: '#F5A623', bg: 'bg-orange-50', link: '/dog-matcher' },
            {
              icon: BookOpen,
              title: 'Learn Breeds',
              desc: siteStats.breeds === null
                ? 'Explore our breed encyclopedia'
                : `Explore our encyclopedia of ${formatStatCount(siteStats.breeds)} ${pluralize(siteStats.breeds, 'breed')}`,
              color: '#7ED321',
              bg: 'bg-green-50',
              link: '/dog-breeds'
            },
          ].map((f, i) => (
            <Link key={i} to={f.link} className="group bg-white rounded-3xl p-8 shadow-[0_8px_30px_rgb(0,0,0,0.04)] hover:shadow-[0_20px_40px_rgb(0,0,0,0.08)] transition-transform transition-shadow duration-300 hover:-translate-y-1" data-testid={`feature-card-${i}`}>
              <div className={`w-12 h-12 ${f.bg} rounded-2xl flex items-center justify-center mb-4`}>
                <f.icon className="w-6 h-6" style={{ color: f.color }} />
              </div>
              <h3 className="font-['Nunito'] font-bold text-lg text-[#1A1D20] mb-1">{f.title}</h3>
              <p className="text-sm text-[#5C636A]">{f.desc}</p>
            </Link>
          ))}
        </div>
      </section>

      {/* Featured Listings */}
      {featuredListings.length > 0 && (
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16" data-testid="featured-listings">
          <div className="flex items-end justify-between mb-8">
            <div>
              <h2 className="font-['Nunito'] font-extrabold text-2xl sm:text-3xl text-[#1A1D20] tracking-tight">
                Featured Dogs
              </h2>
              <p className="text-sm text-[#5C636A] mt-1">Recently posted puppies and dogs for sale</p>
            </div>
            <Link to="/dogs-for-sale" className="hidden sm:flex items-center gap-1 text-sm font-medium text-[#4A90E2] hover:underline" data-testid="view-all-listings">
              View All <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {featuredListings.slice(0, 8).map((listing) => (
              <ListingCard key={listing.listing_id} listing={listing} />
            ))}
          </div>
          <div className="sm:hidden mt-6 text-center">
            <Link to="/dogs-for-sale" className="text-sm font-medium text-[#4A90E2] hover:underline">
              View All Listings <ArrowRight className="w-4 h-4 inline" />
            </Link>
          </div>
        </section>
      )}

      {/* Popular Breeds */}
      {popularBreeds.length > 0 && (
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16" data-testid="popular-breeds">
          <div className="flex items-end justify-between mb-8">
            <div>
              <h2 className="font-['Nunito'] font-extrabold text-2xl sm:text-3xl text-[#1A1D20] tracking-tight">
                Popular Breeds
              </h2>
              <p className="text-sm text-[#5C636A] mt-1">Explore the most popular dog breeds</p>
            </div>
            <Link to="/dog-breeds" className="hidden sm:flex items-center gap-1 text-sm font-medium text-[#4A90E2] hover:underline" data-testid="view-all-breeds">
              All Breeds <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
            {popularBreeds.map((breed) => (
              <BreedCard key={breed.slug} breed={breed} />
            ))}
          </div>
        </section>
      )}

      {/* CTA Section */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="bg-[#4A90E2] rounded-3xl p-8 sm:p-12 text-center relative overflow-hidden">
          <div className="relative z-10">
            <h2 className="font-['Nunito'] font-extrabold text-2xl sm:text-3xl text-white tracking-tight mb-3">
              Ready to Find Your Perfect Dog?
            </h2>
            <p className="text-white/80 text-base mb-6 max-w-md mx-auto">
              Take our quick quiz and we'll match you with breeds that fit your lifestyle
            </p>
            <Button
              onClick={() => navigate('/dog-matcher')}
              className="rounded-full bg-[#F5A623] hover:bg-[#e09515] text-white font-bold px-8 h-12 shadow-[0_4px_14px_rgb(245,166,35,0.4)] cta-button text-base"
              data-testid="cta-dog-matcher"
            >
              Take the Quiz <ArrowRight className="w-4 h-4 ml-1" />
            </Button>
          </div>
          <div className="absolute inset-0 opacity-10">
            <div className="absolute -top-20 -right-20 w-64 h-64 bg-white rounded-full" />
            <div className="absolute -bottom-20 -left-20 w-48 h-48 bg-white rounded-full" />
          </div>
        </div>
      </section>
    </div>
  );
}
